import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute
} from 'amazon-cognito-identity-js';

class CognitoAuthService {
  constructor() {
    // 从环境变量或配置文件获取Cognito配置
    this.userPool = new CognitoUserPool({
      UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || 'ap-southeast-2_Dl5z3CcVK',
      ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID || '7qedgqh9jmjoa6e9tas4k1gqsa'
    });
    
    // Google Sign-In configuration
    this.googleConfig = {
      clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID || '',
      scope: 'email profile openid',
      redirectUri: process.env.REACT_APP_GOOGLE_REDIRECT_URI || `${window.location.origin}/google-callback.html`
    };
  }

  // Google Sign-In methods
  async initiateGoogleSignIn() {
    return new Promise((resolve, reject) => {
      try {
        // Create Google OAuth URL
        const googleAuthUrl = this.buildGoogleAuthUrl();
        
        // Open Google OAuth popup
        const popup = window.open(
          googleAuthUrl,
          'googleSignIn',
          'width=500,height=600,scrollbars=yes,resizable=yes'
        );

        // Listen for the OAuth callback
        const checkClosed = setInterval(() => {
          try {
            if (popup.closed) {
              clearInterval(checkClosed);
              reject(new Error('Google Sign-In was cancelled'));
            }
          } catch (error) {
            // Handle Cross-Origin-Opener-Policy error
            console.log('Popup status check blocked by browser policy');
          }
        }, 1000);

        // Listen for message from popup
        const messageListener = (event) => {
          if (event.origin !== window.location.origin) return;
          
                     if (event.data.type === 'GOOGLE_SIGN_IN_SUCCESS') {
             clearInterval(checkClosed);
             window.removeEventListener('message', messageListener);
             try {
               popup.close();
             } catch (error) {
               // Handle Cross-Origin-Opener-Policy error
               console.log('Popup close blocked by browser policy');
             }
             
             // Exchange Google code for Cognito tokens
             this.exchangeGoogleCodeForTokens(event.data.code)
               .then(resolve)
               .catch(reject);
                     } else if (event.data.type === 'GOOGLE_SIGN_IN_ERROR') {
             clearInterval(checkClosed);
             window.removeEventListener('message', messageListener);
             try {
               popup.close();
             } catch (error) {
               // Handle Cross-Origin-Opener-Policy error
               console.log('Popup close blocked by browser policy');
             }
             reject(new Error(event.data.error || 'Google Sign-In failed'));
          }
        };

        window.addEventListener('message', messageListener);
      } catch (error) {
        reject(error);
      }
    });
  }

  buildGoogleAuthUrl() {
    const params = new URLSearchParams({
      client_id: this.googleConfig.clientId,
      redirect_uri: this.googleConfig.redirectUri,
      response_type: 'code',
      scope: this.googleConfig.scope,
      state: this.generateState(),
      access_type: 'offline',
      prompt: 'select_account'
    });

    return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  }

  generateState() {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }

  async exchangeGoogleCodeForTokens(code) {
    try {
      // Use the correct backend URL
      const backendUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
      const response = await fetch(`${backendUrl}/api/auth/google/callback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to exchange Google code for tokens');
      }

      const result = await response.json();
      return {
        accessToken: result.accessToken,
        idToken: result.idToken,
        refreshToken: result.refreshToken,
        cognitoUserId: result.cognitoUserId,
        user: result.user,
        stripe: result.stripe, // 包含Stripe相关数据
        isNewUser: result.isNewUser // 🔧 添加 isNewUser 字段
      };
    } catch (error) {
      console.error('Error exchanging Google code for tokens:', error);
      throw error;
    }
  }

  // Handle Google OAuth callback (called from popup)
  handleGoogleCallback() {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const error = urlParams.get('error');
    const state = urlParams.get('state');

    if (error) {
      window.opener.postMessage({
        type: 'GOOGLE_SIGN_IN_ERROR',
        error: error
      }, window.opener.location.origin);
    } else if (code) {
      window.opener.postMessage({
        type: 'GOOGLE_SIGN_IN_SUCCESS',
        code: code,
        state: state
      }, window.opener.location.origin);
    }
  }

  // 用户注册
  async registerUser(email, password, fullName, phone) {
    return new Promise((resolve, reject) => {
      const attributeList = [
        new CognitoUserAttribute({
          Name: 'email',
          Value: email
        }),
        new CognitoUserAttribute({
          Name: 'name',
          Value: fullName
        }),
        new CognitoUserAttribute({
          Name: 'phone_number',
          Value: phone
        })
      ];

      // 生成唯一的用户名（因为用户池配置了email alias）
      const username = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      this.userPool.signUp(username, password, attributeList, null, (err, result) => {
        if (err) {
          console.error('Cognito registration error:', err);
          reject(err);
        } else {
          console.log('Cognito registration successful:', result.user.getUsername());
          console.log('User confirmed status:', result.userConfirmed);
          console.log('Registration result:', result);
          resolve({
            cognitoUserId: result.user.getUsername(),
            userConfirmed: result.userConfirmed,
            email: email // 返回email以便后续使用
          });
        }
      });
    });
  }

  // 用户登录
  async loginUser(email, password) {
    return new Promise((resolve, reject) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: email,
        Password: password
      });

      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: this.userPool
      });

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (session) => {
          console.log('Cognito login successful');
          resolve({
            accessToken: session.getAccessToken().getJwtToken(),
            idToken: session.getIdToken().getJwtToken(),
            refreshToken: session.getRefreshToken().getToken(),
            cognitoUserId: cognitoUser.getUsername()
          });
        },
        onFailure: (err) => {
          console.error('Cognito login error:', err);
          reject(err);
        }
      });
    });
  }

  // 忘记密码
  async forgotPassword(email) {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: this.userPool
      });

      cognitoUser.forgotPassword({
        onSuccess: (data) => {
          console.log('Password reset initiated successfully');
          resolve({ success: true, message: 'Password reset email sent' });
        },
        onFailure: (err) => {
          console.error('Password reset error:', err);
          reject(err);
        }
      });
    });
  }

  // 重置密码
  async resetPassword(email, verificationCode, newPassword) {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: this.userPool
      });

      cognitoUser.confirmPassword(verificationCode, newPassword, {
        onSuccess: () => {
          console.log('Password reset successful');
          resolve({ success: true, message: 'Password reset successful' });
        },
        onFailure: (err) => {
          console.error('Password reset error:', err);
          reject(err);
        }
      });
    });
  }

  // 确认用户
  async confirmUser(username, verificationCode) {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: this.userPool
      });

      console.log('Attempting to confirm user with code:', verificationCode);
      console.log('User username:', username);
      console.log('CognitoUser object:', cognitoUser);
      
      cognitoUser.confirmRegistration(verificationCode, true, (err, result) => {
        if (err) {
          console.error('User confirmation error:', err);
          console.error('Error details:', {
            name: err.name,
            message: err.message,
            code: err.code
          });
          reject(err);
        } else {
          console.log('User confirmation successful');
          console.log('Confirmation result:', result);
          resolve(result);
        }
      });
    });
  }

  // 获取当前用户
  getCurrentUser() {
    return this.userPool.getCurrentUser();
  }

  // 获取当前会话
  async getCurrentSession() {
    return new Promise((resolve, reject) => {
      const currentUser = this.getCurrentUser();
      if (currentUser) {
        currentUser.getSession((err, session) => {
          if (err) {
            reject(err);
          } else {
            resolve(session);
          }
        });
      } else {
        resolve(null);
      }
    });
  }

  // 登出
  async logout() {
    return new Promise((resolve, reject) => {
      const currentUser = this.getCurrentUser();
      if (currentUser) {
        currentUser.signOut();
        resolve();
      } else {
        resolve();
      }
    });
  }

  // 重新发送确认码
  async resendConfirmationCode(email) {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: this.userPool
      });

      console.log('Resending confirmation code for email:', email, 'at', new Date().toISOString());
      
      cognitoUser.resendConfirmationCode((err, result) => {
        if (err) {
          console.error('Resend confirmation code error:', err);
          reject(err);
        } else {
          console.log('Confirmation code resent successfully at', new Date().toISOString());
          console.log('Resend result:', result);
          resolve(result);
        }
      });
    });
  }

  // 检查用户状态
  async getUserStatus(email) {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: this.userPool
      });

      console.log('Checking user status for:', email, 'at', new Date().toISOString());
      
      cognitoUser.getUserAttributes((err, attributes) => {
        if (err) {
          console.error('Get user attributes error:', err);
          console.error('Error details:', {
            name: err.name,
            message: err.message,
            code: err.code
          });
          reject(err);
        } else {
          console.log('User attributes retrieved at', new Date().toISOString());
          console.log('User attributes:', attributes);
          
          // Check if user is confirmed by looking for email_verified attribute
          const emailVerified = attributes.find(attr => attr.getName() === 'email_verified');
          console.log('Email verified status:', emailVerified ? emailVerified.getValue() : 'Not found');
          
          resolve(attributes);
        }
      });
    });
  }

  // 检查用户是否需要确认
  async checkUserConfirmationStatus(email) {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: this.userPool
      });

      console.log('Checking confirmation status for:', email, 'at', new Date().toISOString());
      
      // Try to get user attributes to see if user exists and is confirmed
      cognitoUser.getUserAttributes((err, attributes) => {
        if (err) {
          console.log('User attributes error (might be unconfirmed):', err.message);
          // If we can't get attributes, user might be unconfirmed
          resolve({ needsConfirmation: true, error: err.message });
        } else {
          console.log('User attributes found - checking confirmation status');
          const emailVerified = attributes.find(attr => attr.getName() === 'email_verified');
          const isConfirmed = emailVerified && emailVerified.getValue() === 'true';
          
          console.log('User confirmation status:', {
            emailVerified: emailVerified ? emailVerified.getValue() : 'Not found',
            isConfirmed: isConfirmed,
            needsConfirmation: !isConfirmed
          });
          
          resolve({ 
            needsConfirmation: !isConfirmed, 
            isConfirmed: isConfirmed,
            attributes: attributes 
          });
        }
      });
    });
  }

  // 验证token
  async verifyToken(token) {
    return new Promise((resolve, reject) => {
      try {
        // 解析JWT token
        const tokenParts = token.split('.');
        if (tokenParts.length !== 3) {
          reject(new Error('Invalid token format'));
          return;
        }

        const payload = JSON.parse(atob(tokenParts[1]));
        const currentTime = Math.floor(Date.now() / 1000);

        // 检查token是否过期
        if (payload.exp && payload.exp < currentTime) {
          reject(new Error('Token expired'));
          return;
        }

        resolve({
          isValid: true,
          cognitoUserId: payload.sub,
          email: payload.email,
          exp: payload.exp
        });
      } catch (error) {
        console.error('Token verification error:', error);
        reject(error);
      }
    });
  }
}

export default CognitoAuthService; 